import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { User, Group, Expense, ActivityLog, RecurrenceInterval } from '../types';
import { authService } from '../services/authService';
import { dataService } from '../services/dataService';
import { isFirebaseEnabled, auth, requestNotificationPermission, messaging, getMessagingInstance } from '../services/firebase';
import { fetchRealTimeRates, suggestRegionSettings } from '../services/geminiService';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { processDueRecurringExpenses } from '../utils/recurrenceUtils';

// Safely retrieve the initial user state, handling potential localStorage corruption.
const getInitialUser = (): User | null => {
  try {
    if (localStorage.getItem('paycircle_session_expired') === 'true') {
      return null;
    }
    const saved = localStorage.getItem('paycircle_session_user');
    if (!saved) return null;
    return JSON.parse(saved);
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    localStorage.removeItem('paycircle_session_user');
    localStorage.removeItem('paycircle_session_expiry');
    localStorage.removeItem('paycircle_session_timestamp'); // legacy cleanup
    return null;
  }
};

const FALLBACK_RATES: Record<string, number> = { USD: 1, EUR: 0.92, GBP: 0.79, INR: 83.5, JPY: 150, CAD: 1.35, AUD: 1.52, CNY: 7.2, CHF: 0.88, SGD: 1.34 };

interface AppSettings {
  notifications: boolean;
  emailDigest: boolean;
  darkMode: boolean;
  logoStyle?: string;
  appName: string;
  appLogo: string;
}

const DEFAULT_SETTINGS: AppSettings = {
  notifications: true,
  emailDigest: false,
  darkMode: false,
  logoStyle: 'CUSTOM',
  appName: 'PayCircle',
  appLogo: ''
};

interface AddFriendResult { status: 'added' | 'not_found' | 'exists'; user?: User; }

interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => Promise<void>;
  confirmText?: string;
  cancelText?: string;
}

interface AppState {
  currentUser: User;
  users: User[];
  groups: Group[];
  expenses: Expense[];
  activities: ActivityLog[];
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isSessionChecking: boolean;
  isFirebaseActive: boolean;
  isOnline: boolean;
  loginWithGoogle: () => Promise<void>;
  loginWithEmail: (e: string, p: string) => Promise<void>;
  register: (e: string, p: string, n: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  loginAsOfflineGuest: (name?: string) => Promise<void>;
  logout: () => void;
  updateCurrentUser: (updates: Partial<User>) => void;
  appSettings: AppSettings;
  updateAppSettings: (settings: Partial<AppSettings>) => void;
  userCurrency: string;
  setUserCurrency: (currency: string) => void;
  availableCurrencies: string[];
  exchangeRates: Record<string, number>;
  isLoadingRates: boolean;
  refreshRates: () => Promise<void>;
  ratesLastUpdated: Date | null;
  isDataLoading: boolean;
  isInstallable: boolean;
  isIOS: boolean;
  isStandalone: boolean;
  installApp: () => void;
  showIOSInstallGuide: boolean;
  setShowIOSInstallGuide: (show: boolean) => void;
  addExpense: (expense: Expense) => Promise<void>;
  updateExpense: (id: string, updates: Partial<Expense>) => Promise<void>;
  deleteExpense: (expenseId: string) => Promise<void>;
  addGroup: (group: Group) => Promise<void>;
  updateGroup: (groupId: string, updates: Partial<Group>) => Promise<void>;
  deleteGroup: (groupId: string) => Promise<void>;
  joinGroup: (groupId: string) => Promise<boolean>;
  addFriend: (email: string) => Promise<AddFriendResult>;
  getGroupExpenses: (groupId: string) => Expense[];
  getUserBalance: (userId: string) => number;
  getSplitBalances: () => { totalOwed: number, totalOwe: number };
  getCurrencySymbol: (code: string) => string;
  getCurrencyName: (code: string) => string;
  convertToUserCurrency: (amount: number, fromCurrency: string) => number;
  settleUp: (payerId: string, receiverId: string, amount: number, currency: string, groupId?: string, recurrence?: RecurrenceInterval, nextRecurringDate?: string) => Promise<void>;
  t: (key: string, options?: any) => string;
  confirmation: ConfirmationState;
  requestConfirmation: (options: Omit<ConfirmationState, 'isOpen'>) => void;
  closeConfirmation: () => void;
  detectRegionSettings: () => Promise<void>;
  processRecurringExpenses: () => Promise<void>;
  isDetectingLocation: boolean;
  addNotification?: (message: string, type: 'success' | 'error' | 'info') => void;
}

const AppContext = createContext<AppState | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode, addNotification?: (message: string, type: 'success' | 'error' | 'info') => void }> = ({ children, addNotification }) => {
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<User>(() => getInitialUser() || { id: 'guest', name: 'Guest', email: '', avatar: '', language: 'en' });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!getInitialUser());
  const [isSessionChecking, setIsSessionChecking] = useState<boolean>(isFirebaseEnabled);
  // Ref to debounce Firebase Auth null events so transient token refreshes don't trigger a logout.
  const firebaseNullLogoutTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>(() => {
    try {
      const stored = localStorage.getItem('paycircle_users');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  const [groups, setGroups] = useState<Group[]>(() => {
    try {
      const stored = localStorage.getItem('paycircle_groups');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  const [expenses, setExpenses] = useState<Expense[]>(() => {
    try {
      const stored = localStorage.getItem('paycircle_expenses');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  const [activities, setActivities] = useState<ActivityLog[]>(() => {
    try {
      const stored = localStorage.getItem('paycircle_activities');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  const [isDataLoading, setIsDataLoading] = useState(() => {
    try {
      const storedUser = getInitialUser();
      if (!storedUser) return false;
      const cachedGroups = localStorage.getItem('paycircle_groups');
      return !cachedGroups;
    } catch (e) {
      return true;
    }
  });

  const [appSettings, setAppSettingsState] = useState<AppSettings>(() => {
    const saved = localStorage.getItem('paycircle_settings');
    if (!saved) return DEFAULT_SETTINGS;
    try {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_SETTINGS, ...parsed };
    } catch (e) {
      return DEFAULT_SETTINGS;
    }
  });

  const [userCurrency, setUserCurrencyState] = useState<string>(() => localStorage.getItem('paycircle_currency') || 'USD');
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>(FALLBACK_RATES);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [ratesLastUpdated, setRatesLastUpdated] = useState<Date | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSInstallGuide, setShowIOSInstallGuide] = useState(false);
  const { t: i18nextT, i18n } = useTranslation();
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [confirmation, setConfirmation] = useState<ConfirmationState>({ isOpen: false, title: '', message: '', onConfirm: async () => { } });

  const requestConfirmation = useCallback((options: Omit<ConfirmationState, 'isOpen'>) => {
    setConfirmation({ ...options, isOpen: true });
  }, []);

  const closeConfirmation = useCallback(() => {
    setConfirmation(prev => ({ ...prev, isOpen: false }));
  }, []);

  const t = useCallback((key: string, options?: any) => {
    if (key === 'app_name') return appSettings.appName || 'PayCircle';
    return i18nextT(key, { ...options, defaultValue: key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) });
  }, [i18nextT, appSettings.appName]);

  const logout = useCallback(async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setCurrentUser({ id: 'guest', name: t('default_user_name'), email: '', avatar: '', language: 'en' });
  }, [t]);

  const refreshRates = useCallback(async () => {
    if (!navigator.onLine) return;
    setIsLoadingRates(true);
    try {
      const realTimeRates = await fetchRealTimeRates();
      if (Object.keys(realTimeRates).length > 5) { setExchangeRates(prev => ({ ...prev, ...realTimeRates })); setRatesLastUpdated(new Date()); } else { const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD'); if (!response.ok) throw new Error(t('sync_failed')); const data = await response.json(); setExchangeRates(data.rates); setRatesLastUpdated(new Date()); }
    } catch (error) { console.warn(t('sync_failed'), error); } finally { setIsLoadingRates(false); }
  }, [t]);

  useEffect(() => {
    const handleOnline = () => {
      addNotification?.(t('data_resynced'), 'success');
      setIsOnline(true);
      if (isFirebaseEnabled && auth?.currentUser) {
        auth.currentUser.getIdToken().catch(() => {});
      }
      refreshRates();
    };
    const handleOffline = () => {
      addNotification?.(t('offline_first_desc'), 'warning');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [addNotification, refreshRates, t]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const resetTimer = () => authService.updateSessionActivity();
    // For Firebase users, Firebase manages session lifetime (token auto-refresh every ~1h).
    // Applying our local time-based check on top causes false logouts during token refreshes.
    // Only run the local expiry check in non-Firebase (localStorage fallback) mode.
    const checkSession = () => { if (!isFirebaseEnabled && authService.isSessionExpired()) { logoutRef.current(); } };
    const intervalId = setInterval(checkSession, 60 * 1000);

    // Check session immediately when app becomes visible or focused (critical for PWAs resumed from background).
    // IMPORTANT: reset the timer BEFORE checking expiry so normal active users aren't booted.
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        resetTimer();  // extend session first
        if (isFirebaseEnabled && auth?.currentUser) {
          auth.currentUser.getIdToken().catch(err => {
            if (err?.code === 'auth/user-not-found' || err?.code === 'auth/user-disabled') {
              logoutRef.current();
            } else if (!err?.message?.includes('securetoken.googleapis.com')) {
              console.warn('[Auth] Token verification on resume:', err);
            }
          });
        }
        checkSession(); // then verify (will only expire if truly over the limit — Firebase users never hit this)
      }
    };

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('click', resetTimer);
    window.addEventListener('touchstart', resetTimer);
    window.addEventListener('scroll', resetTimer);
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('touchstart', resetTimer);
      window.removeEventListener('scroll', resetTimer);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  // Use logoutRef (not logout directly) so this effect is only tied to isAuthenticated.
  // If logout was in deps, every t() change would re-register all event listeners.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    const syncPrompt = () => {
      const stashed = (window as any).deferredPWAInstallPrompt;
      if (stashed && !deferredPrompt) {
        setDeferredPrompt(stashed);
      }
    };

    syncPrompt();
    const poll = setInterval(syncPrompt, 1000);

    const onPromptReady = (e: any) => {
      setDeferredPrompt(e.detail || (window as any).deferredPWAInstallPrompt);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      (window as any).deferredPWAInstallPrompt = null;
    };

    window.addEventListener('pwa-prompt-ready', onPromptReady);
    window.addEventListener('pwa-installed', onInstalled);

    const userAgent = window.navigator.userAgent.toLowerCase();
    const isSafariOnIOS = () => {
      return /iphone|ipad|ipod/.test(userAgent) &&
        !/crios/.test(userAgent) &&
        !/fxios/.test(userAgent) &&
        (window as any).safari !== undefined;
    }

    setIsIOS(isSafariOnIOS());
    setIsStandalone(window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true);

    return () => {
      window.removeEventListener('pwa-prompt-ready', onPromptReady);
      window.removeEventListener('pwa-installed', onInstalled);
      clearInterval(poll);
    };
  }, [deferredPrompt]);

  const installApp = useCallback(async () => {
    if (isIOS) { setShowIOSInstallGuide(true); return; }
    const prompt = deferredPrompt || (window as any).deferredPWAInstallPrompt;

    if (!prompt) {
      addNotification?.(t('install_app_desc'), "info");
      return;
    }

    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      console.log(`User response to install prompt: ${outcome}`);
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        (window as any).deferredPWAInstallPrompt = null;
      }
    } catch (e) {
      console.error("Install prompt error:", e);
      addNotification?.(t('install_app_desc'), "info");
    }
  }, [deferredPrompt, isIOS, addNotification, t]);

  useEffect(() => {
    const lang = currentUser.language || 'en';
    i18n.changeLanguage(lang);
    document.documentElement.dir = ['ar', 'ur'].includes(lang) ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [currentUser.language, i18n]);

  // Stable refs for callbacks used inside the onAuthStateChanged subscription.
  // This prevents the effect from re-running (and re-subscribing) when t or logout change.
  const tRef = React.useRef(t);
  tRef.current = t;
  const logoutRef = React.useRef(logout);
  logoutRef.current = logout;



  useEffect(() => {
    if (isFirebaseEnabled && auth) {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        // For Firebase mode: trust Firebase as the session authority.
        // Only honour the explicit sign-out flag here — NOT the local time-based check.
        // The time-based check is only meaningful for the local (non-Firebase) fallback mode,
        // and applying it here causes logouts during Firebase's own hourly token refresh cycle.
        const isExpired = localStorage.getItem('paycircle_session_expired') === 'true';
        if (isExpired) {
          if (auth) {
            try { await signOut(auth); } catch (_) { /* ignore */ }
          }
          localStorage.removeItem('paycircle_session_user');
          localStorage.removeItem('paycircle_session_expiry');
          localStorage.removeItem('paycircle_session_expired');
          localStorage.removeItem('paycircle_session_timestamp'); // legacy cleanup
          setIsSessionChecking(false);
          setIsAuthenticated(false);
          setCurrentUser({ id: 'guest', name: tRef.current('default_user_name'), email: '', avatar: '', language: 'en' });
          return;
        }
        if (user) {
          // A real authenticated user arrived — cancel any pending null-logout.
          if (firebaseNullLogoutTimer.current) {
            clearTimeout(firebaseNullLogoutTimer.current);
            firebaseNullLogoutTimer.current = null;
          }
          setCurrentUser(prev => {
            if (prev.id && prev.id !== user.uid && (prev.id === 'guest' || prev.id.startsWith('guest_'))) {
              dataService.migrateGuestData(prev.id, user.uid).catch(err => {
                console.error("Guest data migration error:", err);
              });
            }
            const freshUser: User = {
              ...prev,
              id: user.uid,
              name: user.displayName || prev.name || t('default_user_name'),
              email: user.email || prev.email || '',
              avatar: user.photoURL || prev.avatar || `https://ui-avatars.com/api/?name=User`
            };
            localStorage.setItem('paycircle_session_user', JSON.stringify(freshUser));

            // Unconditionally refresh the session activity timestamp upon successful restoration/loading
            authService.updateSessionActivity();
            return freshUser;
          });
          setIsAuthenticated(true);
          setIsSessionChecking(false);
        } else {
          // Firebase Auth returned null (offline or transient initialization) — preserve local cached session if available
          const cachedSession = getInitialUser();
          if (cachedSession) {
            console.log('[Auth] Firebase Auth returned null, maintaining local cached session for offline/persistent access.');
            setCurrentUser(cachedSession);
            setIsAuthenticated(true);
          } else {
            console.log('[Auth] Firebase Auth returned null with no cached session. Maintaining guest state.');
            setIsAuthenticated(false);
            setCurrentUser({ id: 'guest', name: tRef.current('default_user_name'), email: '', avatar: '', language: 'en' });
          }
          setIsSessionChecking(false);
        }
      });

      // Wait for the initial authentication state to settle (offline-first & extremely robust)
      if (typeof auth.authStateReady === 'function') {
        auth.authStateReady().then(() => {
          if (auth.currentUser) {
            // Session successfully restored from persistent storage (IndexedDB/localStorage)
            setIsSessionChecking(false);
          } else {
            const cachedSession = getInitialUser();
            if (cachedSession) {
              console.log('[Auth] authStateReady null, maintaining local cached session.');
              setCurrentUser(cachedSession);
              setIsAuthenticated(true);
            } else {
              console.log('[Auth] authStateReady null with no cached user — keeping guest state.');
              setIsAuthenticated(false);
              setCurrentUser({ id: 'guest', name: tRef.current('default_user_name'), email: '', avatar: '', language: 'en' });
            }
            setIsSessionChecking(false);
          }
        }).catch((err) => {
          console.error('[Auth] authStateReady failed:', err);
          setIsSessionChecking(false);
        });
      } else {
        // Fallback for older Firebase versions that do not support authStateReady
        console.log('[Auth] authStateReady not supported, using fallback check.');
        const cachedUser = getInitialUser();
        if (cachedUser) {
          setCurrentUser(cachedUser);
          setIsAuthenticated(true);
        }
        setIsSessionChecking(false);
      }

      return () => unsubscribe();
    } else {
      // Local fallback mode: check and handle session expiration on initial load
      const storedSession = localStorage.getItem('paycircle_session_user');
      const isExpired = localStorage.getItem('paycircle_session_expired') === 'true' || authService.isSessionExpired();
      if (isExpired) {
        localStorage.removeItem('paycircle_session_user');
        localStorage.removeItem('paycircle_session_expiry');
        localStorage.removeItem('paycircle_session_expired');
        localStorage.removeItem('paycircle_session_timestamp'); // legacy cleanup
        setCurrentUser({ id: 'guest', name: tRef.current('default_user_name'), email: '', avatar: '', language: 'en' });
        setIsAuthenticated(false);
      } else if (storedSession) {
        // If the session is still valid, extend it for another week from this load!
        authService.updateSessionActivity();
      }
      setIsSessionChecking(false);
      setIsDataLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps — subscribe once on mount. Uses refs for t/logout.

  const users = useMemo(() => {
    if (!isAuthenticated || currentUser.id === 'guest') return [];
    const associatedIds = new Set<string>();
    if (currentUser.friends) currentUser.friends.forEach(id => associatedIds.add(id));
    groups.forEach(g => { if (g.members) g.members.forEach(id => associatedIds.add(id)); });
    expenses.forEach(e => { associatedIds.add(e.payerId); if (e.splitWith) e.splitWith.forEach(id => associatedIds.add(id)); });
    return allUsers.filter(u => u.id === currentUser.id || associatedIds.has(u.id));
  }, [allUsers, currentUser, groups, expenses, isAuthenticated]);

  // Dynamically compute the required user IDs we need to subscribe to.
  const requiredUserIds = useMemo(() => {
    if (!isAuthenticated || currentUser.id === 'guest') return [];
    const ids = new Set<string>([currentUser.id]);
    if (currentUser.friends) currentUser.friends.forEach(id => ids.add(id));
    groups.forEach(g => { if (g.members) g.members.forEach(id => ids.add(id)); });
    expenses.forEach(e => { ids.add(e.payerId); if (e.splitWith) e.splitWith.forEach(id => ids.add(id)); });
    return Array.from(ids).filter(Boolean);
  }, [currentUser, groups, expenses, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && currentUser.id !== 'guest' && !isSessionChecking) {
      if (groups.length === 0) {
        setIsDataLoading(true);
      }
      const unsubGroups = dataService.listenToGroups(currentUser.id, (userGroups) => { setGroups(userGroups); });
      const unsubActivities = dataService.listenToActivities(currentUser.id, setActivities);
      const loadingTimeout = setTimeout(() => setIsDataLoading(false), 1500);
      return () => { unsubGroups(); unsubActivities(); clearTimeout(loadingTimeout); };
    } else if (!isSessionChecking) {
      setGroups([]); setExpenses([]); setActivities([]); setAllUsers([]); setIsDataLoading(false);
    }
  }, [isAuthenticated, currentUser.id, isSessionChecking, auth?.currentUser?.uid]);

  const groupIdsKey = useMemo(() => groups.map(g => g.id).sort().join(','), [groups]);

  useEffect(() => {
    if (!isAuthenticated || currentUser.id === 'guest') return;
    const groupIds = groups.map(g => g.id);
    const unsubExpenses = dataService.listenToExpenses(currentUser.id, groupIds, setExpenses);
    return () => unsubExpenses();
  }, [isAuthenticated, currentUser.id, groupIdsKey, auth?.currentUser?.uid]);

  useEffect(() => {
    if (!isAuthenticated || currentUser.id === 'guest' || requiredUserIds.length === 0) return;
    const unsubUsers = dataService.listenToUsers(requiredUserIds, (receivedUsers) => {
      const selfUser = receivedUsers.find(u => u.id === currentUser.id);
      if (!selfUser) {
        dataService.addUser(currentUser);
      } else {
        setCurrentUser(prev => ({ ...prev, ...selfUser, id: prev.id, email: prev.email }));
        if (selfUser.defaultCurrency && selfUser.defaultCurrency !== userCurrency) {
          setUserCurrencyState(selfUser.defaultCurrency);
          localStorage.setItem('paycircle_currency', selfUser.defaultCurrency);
        }
      }
      setAllUsers(receivedUsers);
    });
    return () => unsubUsers();
  }, [isAuthenticated, currentUser.id, requiredUserIds.join(','), userCurrency]);

  // Automatic FCM Token Registration & Sync
  useEffect(() => {
    if (isAuthenticated && currentUser.id !== 'guest' && appSettings.notifications && isFirebaseEnabled) {
      const timer = setTimeout(() => {
        requestNotificationPermission(currentUser.id).catch(err => {
          console.error("Auto notification token registration failed:", err);
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, currentUser.id, appSettings.notifications]);

  // Foreground Notification Listener
  useEffect(() => {
    if (isFirebaseEnabled && isAuthenticated) {
      let unsubscribe: (() => void) | undefined;
      getMessagingInstance().then((messagingInstance) => {
        if (messagingInstance) {
          import('firebase/messaging').then(({ onMessage }) => {
            unsubscribe = onMessage(messagingInstance, (payload) => {
              console.log('Foreground message received:', payload);
              if (payload.notification) {
                const body = payload.notification.body || '';
                addNotification?.(body, 'info');
              }
            });
          }).catch(console.error);
        }
      }).catch(console.error);

      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [isAuthenticated, addNotification]);

  useEffect(() => {
    const root = window.document.documentElement;
    if (appSettings.darkMode) root.classList.add('dark'); else root.classList.remove('dark');
    localStorage.setItem('paycircle_settings', JSON.stringify(appSettings));
  }, [appSettings]);

  useEffect(() => { refreshRates(); }, [refreshRates]);

  const handleAuthSuccess = useCallback((user: User) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    authService.updateSessionActivity();
    if (user.defaultCurrency) {
      setUserCurrencyState(user.defaultCurrency);
      localStorage.setItem('paycircle_currency', user.defaultCurrency);
    }
  }, []);

  const loginWithGoogle = useCallback(async () => { setIsAuthLoading(true); try { const user = await authService.loginWithGoogle(); handleAuthSuccess(user); } finally { setIsAuthLoading(false); } }, [handleAuthSuccess]);
  const loginWithEmail = useCallback(async (email: string, pass: string) => { setIsAuthLoading(true); try { const user = await authService.loginWithEmail(email, pass); handleAuthSuccess(user); } finally { setIsAuthLoading(false); } }, [handleAuthSuccess]);
  const register = useCallback(async (email: string, pass: string, name: string) => { setIsAuthLoading(true); try { const user = await authService.register(email, pass, name); handleAuthSuccess(user); } finally { setIsAuthLoading(false); } }, [handleAuthSuccess]);
  const resetPassword = useCallback(async (email: string) => { setIsAuthLoading(true); try { await authService.resetPassword(email); } finally { setIsAuthLoading(false); } }, []);
  const loginAsOfflineGuest = useCallback(async (customName?: string) => { setIsAuthLoading(true); try { const user = await authService.loginAsOfflineGuest(customName); handleAuthSuccess(user); } finally { setIsAuthLoading(false); } }, [handleAuthSuccess]);

  const setUserCurrency = useCallback((currency: string) => {
    setUserCurrencyState(currency); localStorage.setItem('paycircle_currency', currency); if (currentUser.id && currentUser.id !== 'guest') { dataService.updateUser(currentUser.id, { defaultCurrency: currency }); }
  }, [currentUser.id]);

  const updateCurrentUser = useCallback((updates: Partial<User>) => {
    setCurrentUser(prev => {
      const updated = { ...prev, ...updates };
      dataService.updateUser(updated.id, updates);
      return updated;
    });
  }, []);

  const updateAppSettings = useCallback((settings: Partial<AppSettings>) => { setAppSettingsState(prev => ({ ...prev, ...settings })); }, []);

  const detectRegionSettings = useCallback(async () => {
    if (!navigator.geolocation) {
      addNotification?.(t('faq_2_a'), "error");
      return;
    }
    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const { latitude, longitude } = position.coords;
        const settings = await suggestRegionSettings(latitude, longitude);
        if (settings) {
          requestConfirmation({
            title: t('detect_location'),
            message: t('detect_location'),
            confirmText: t('apply'),
            cancelText: t('skip'),
            onConfirm: async () => {
              setUserCurrency(settings.currency);
              updateCurrentUser({ language: settings.language });
              addNotification?.(t('detect_location'), "success");
              closeConfirmation();
            }
          });
        }
      } catch (e) {
        addNotification?.(t('detect_location'), "error");
      } finally {
        setIsDetectingLocation(false);
      }
    }, (error) => {
      setIsDetectingLocation(false);
      let errorMessage = t('detect_location');
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = t('detect_location');
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = t('detect_location');
          break;
        case error.TIMEOUT:
          errorMessage = t('detect_location');
          break;
      }
      addNotification?.(errorMessage, "error");
    }, { timeout: 10000, enableHighAccuracy: false });
  }, [setUserCurrency, updateCurrentUser, addNotification, requestConfirmation, closeConfirmation, t]);

  const ensureFriendships = useCallback(async (userIds: string[]) => {
    if (!currentUser.id || currentUser.id === 'guest') return;
    const newFriendIds = userIds.filter(id => id && id !== currentUser.id && !(currentUser.friends || []).includes(id));
    if (newFriendIds.length === 0) return;
    Promise.all(newFriendIds.map(id => dataService.addFriendConnection(currentUser.id, id))).catch(e => console.error(t('authentication_error'), e));
  }, [currentUser.id, currentUser.friends, t]);

  useEffect(() => {
    if (!isAuthenticated || !currentUser.id || currentUser.id === 'guest') return;
    const idsToSync = new Set<string>();
    groups.forEach(g => g.members?.forEach(mid => idsToSync.add(mid)));
    expenses.forEach(e => { idsToSync.add(e.payerId); e.splitWith?.forEach(uid => idsToSync.add(uid)); });
    if (idsToSync.size > 0) ensureFriendships(Array.from(idsToSync));
  }, [groups, expenses, isAuthenticated, currentUser.id, ensureFriendships]);

  const addFriend = useCallback(async (nameOrEmail: string): Promise<AddFriendResult> => {
    const input = nameOrEmail.trim(); const isEmail = input.includes('@'); const targetEmail = isEmail ? input.toLowerCase() : '';
    if (currentUser.friends) { const alreadyFriend = users.find(u => currentUser.friends?.includes(u.id) && (isEmail ? u.email.toLowerCase() === targetEmail : u.name.toLowerCase() === input.toLowerCase())); if (alreadyFriend) return { status: 'exists', user: alreadyFriend }; }
    let foundUser: User | null = isEmail ? await dataService.findUserByEmail(targetEmail) : null;
    if (!foundUser) {
      const newId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
      const newUser: User = { id: newId, name: isEmail ? input.split('@')[0] : input, email: isEmail ? input : `guest_${newId}@paycircle.local`, avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(input)}&background=random&color=fff`, language: 'en' };
      await dataService.addUser(newUser); foundUser = newUser;
    }
    await dataService.addFriendConnection(currentUser.id, foundUser.id);
    return { status: 'added', user: foundUser };
  }, [users, currentUser]);

  const isProcessingRecurrenceRef = useRef(false);

  const processRecurringExpenses = useCallback(async () => {
    if (!isAuthenticated || currentUser.id === 'guest' || expenses.length === 0 || isProcessingRecurrenceRef.current) return;
    try {
      isProcessingRecurrenceRef.current = true;
      const { newExpenses, updatedParents, newActivities } = processDueRecurringExpenses(expenses, currentUser.id, t, new Date());
      if (newExpenses.length === 0 && updatedParents.length === 0) return;

      if (newExpenses.length > 0) {
        setExpenses(prev => [...newExpenses, ...prev]);
        for (const newExp of newExpenses) {
          await dataService.addExpense(newExp);
        }
      }

      if (updatedParents.length > 0) {
        setExpenses(prev => prev.map(e => {
          const update = updatedParents.find(u => u.id === e.id);
          return update ? { ...e, nextRecurringDate: update.nextRecurringDate } : e;
        }));
        for (const update of updatedParents) {
          await dataService.updateExpense(update.id, { nextRecurringDate: update.nextRecurringDate });
        }
      }

      if (newActivities.length > 0) {
        setActivities(prev => [...newActivities, ...prev]);
        for (const act of newActivities) {
          await dataService.logActivity(act);
        }
        addNotification?.(t('recurring_expense_generated', { title: newExpenses[0]?.description || t('expense') }), 'info');
      }
    } catch (err) {
      console.warn("processRecurringExpenses error:", err);
    } finally {
      isProcessingRecurrenceRef.current = false;
    }
  }, [isAuthenticated, currentUser.id, expenses, t, addNotification]);

  // Trigger recurring expense processor on expense changes
  useEffect(() => {
    if (isAuthenticated && currentUser.id !== 'guest' && expenses.length > 0) {
      processRecurringExpenses();
    }
  }, [isAuthenticated, currentUser.id, expenses.length, processRecurringExpenses]);

  // Periodic recurring check (every 60 seconds)
  useEffect(() => {
    if (!isAuthenticated || currentUser.id === 'guest') return;
    const interval = setInterval(() => {
      processRecurringExpenses();
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, currentUser.id, processRecurringExpenses]);

  const addExpense = useCallback(async (expense: Expense) => {
    const groupMembers = expense.groupId ? (groups.find(g => g.id === expense.groupId)?.members || []) : [];
    const expenseWithUser: Expense = {
      ...expense,
      groupMembers: expense.groupId ? (expense.groupMembers || groupMembers) : undefined,
      lastUpdatedBy: currentUser.id
    };
    // Optimistically update React state immediately
    setExpenses(prev => {
      const exists = prev.some(e => e.id === expenseWithUser.id);
      if (exists) return prev.map(e => e.id === expenseWithUser.id ? expenseWithUser : e);
      return [expenseWithUser, ...prev];
    });

    try {
      await dataService.addExpense(expenseWithUser);
    } catch (err) {
      console.error("Failed to persist added expense:", err);
      addNotification?.(t('sync_failed'), 'error');
      throw err;
    }

    const participants = Array.from(new Set([expenseWithUser.payerId, ...(expenseWithUser.splitWith || [])]));
    ensureFriendships(participants);
    const isPayment = expenseWithUser.type === 'payment' || expenseWithUser.category === 'payment';
    const recurrenceLabel = expenseWithUser.recurrence && expenseWithUser.recurrence !== 'one_time' ? ` (${expenseWithUser.recurrence.toUpperCase()})` : '';
    const newActivity: ActivityLog = {
      id: `act_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      userId: currentUser.id,
      action: isPayment 
        ? (expenseWithUser.recurrence && expenseWithUser.recurrence !== 'one_time' ? `[${expenseWithUser.recurrence}] ${t('settlement')}` : t('settlement'))
        : (expenseWithUser.recurrence && expenseWithUser.recurrence !== 'one_time' ? `[${expenseWithUser.recurrence}] ${t('action_added')} ${t('expense')}` : `${t('action_added')} ${t('expense')}`),
      details: `${expenseWithUser.description}${recurrenceLabel}`,
      timestamp: new Date().toISOString(),
      relatedUserIds: Array.from(new Set([currentUser.id, ...participants, ...groupMembers])),
      groupId: expenseWithUser.groupId,
      recurrence: expenseWithUser.recurrence,
      isRecurringAuto: expenseWithUser.isRecurringAuto,
      expenseId: expenseWithUser.id
    };
    setActivities(prev => [newActivity, ...prev.filter(a => a.id !== newActivity.id)]);
    dataService.logActivity(newActivity).catch(e => console.error(t('authentication_error'), e));
  }, [currentUser.id, groups, ensureFriendships, t, addNotification]);

  const updateExpense = useCallback(async (id: string, updates: Partial<Expense>) => {
    const oldExpense = expenses.find(e => e.id === id);
    const targetGroupId = updates.groupId || oldExpense?.groupId;
    const groupMembers = targetGroupId ? (groups.find(g => g.id === targetGroupId)?.members || []) : [];
    const updatesWithUser: Partial<Expense> = {
      ...updates,
      groupMembers: targetGroupId ? (updates.groupMembers || groupMembers) : undefined,
      lastUpdatedBy: currentUser.id
    };
    // Optimistically update React state immediately
    setExpenses(prev => prev.map(e => e.id === id ? { ...e, ...updatesWithUser } : e));

    try {
      await dataService.updateExpense(id, updatesWithUser);
    } catch (err) {
      console.error("Failed to persist updated expense:", err);
      addNotification?.(t('sync_failed'), 'error');
      throw err;
    }

    const newParticipants = Array.from(new Set([updates.payerId || oldExpense?.payerId || '', ...(updates.splitWith || oldExpense?.splitWith || [])]));
    ensureFriendships(newParticipants);
    if (updates.amount || updates.description || updates.splitWith || updates.recurrence) {
      const rec = updates.recurrence || oldExpense?.recurrence;
      const recLabel = rec && rec !== 'one_time' ? ` (${rec.toUpperCase()})` : '';
      const newActivity: ActivityLog = {
        id: `act_u_${Date.now()}`,
        userId: currentUser.id,
        action: rec && rec !== 'one_time' ? `[${rec}] ${t('action_updated')}` : t('action_updated'),
        details: `${updates.description || oldExpense?.description || t('expense')}${recLabel}`,
        timestamp: new Date().toISOString(),
        relatedUserIds: Array.from(new Set([currentUser.id, ...newParticipants, ...groupMembers])),
        groupId: targetGroupId,
        recurrence: rec,
        expenseId: id
      };
      setActivities(prev => [newActivity, ...prev.filter(a => a.id !== newActivity.id)]);
      dataService.logActivity(newActivity).catch(e => console.error(t('authentication_error'), e));
    }
  }, [currentUser.id, expenses, groups, ensureFriendships, t, addNotification]);

  const deleteExpense = useCallback(async (expenseId: string) => {
    const expenseToDelete = expenses.find(e => e.id === expenseId);
    if (!expenseToDelete) return;
    // Optimistically update React state immediately
    setExpenses(prev => prev.filter(e => e.id !== expenseId));
    try {
      await dataService.deleteExpense(expenseId);
      const groupMembers = expenseToDelete.groupId ? (groups.find(g => g.id === expenseToDelete.groupId)?.members || []) : [];
      const participants = Array.from(new Set([currentUser.id, expenseToDelete.payerId, ...(expenseToDelete.splitWith || []), ...groupMembers]));
      const newActivity: ActivityLog = { id: `act_d_${Date.now()}`, userId: currentUser.id, action: t('action_deleted'), details: expenseToDelete.description, timestamp: new Date().toISOString(), relatedUserIds: participants, groupId: expenseToDelete.groupId };
      setActivities(prev => [newActivity, ...prev.filter(a => a.id !== newActivity.id)]);
      dataService.logActivity(newActivity).catch(e => console.error(t('authentication_error'), e));
      addNotification?.(`${t('expense')} ${t('action_deleted')}.`, 'success');
    } catch (error) { addNotification?.(t('sync_failed'), 'error'); }
  }, [currentUser.id, expenses, groups, addNotification, t]);

  const addGroup = useCallback(async (group: Group) => {
    const newGroup = { ...group, lastUpdatedBy: currentUser.id };
    // Optimistically update React state immediately
    setGroups(prev => {
      const exists = prev.some(g => g.id === group.id);
      if (exists) return prev.map(g => g.id === group.id ? newGroup : g);
      return [newGroup, ...prev];
    });

    await dataService.addGroup(newGroup);
    ensureFriendships(group.members);
    const newActivity: ActivityLog = { id: `act_g_${Date.now()}`, userId: currentUser.id, action: `${t('action_created')} ${t('group')}`, details: group.name, timestamp: new Date().toISOString(), relatedUserIds: group.members, groupId: group.id };
    setActivities(prev => [newActivity, ...prev.filter(a => a.id !== newActivity.id)]);
    dataService.logActivity(newActivity).catch(e => console.error(t('authentication_error'), e));
  }, [currentUser.id, ensureFriendships, t]);

  const updateGroup = useCallback(async (groupId: string, updates: Partial<Group>) => {
    const previousGroup = groups.find(g => g.id === groupId);
    // Optimistically update React state immediately
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...updates, lastUpdatedBy: currentUser.id } : g));

    try {
      await dataService.updateGroup(groupId, { ...updates, lastUpdatedBy: currentUser.id });
      const targetGroup = groups.find(g => g.id === groupId) || previousGroup;
      const allMembers = updates.members || targetGroup?.members || [currentUser.id];
      if (updates.members) ensureFriendships(updates.members);
      const newActivity: ActivityLog = { 
        id: `act_gu_${Date.now()}`, 
        userId: currentUser.id, 
        action: `${t('action_updated')} ${t('group_settings')}`, 
        details: updates.name || targetGroup?.name || t('group'), 
        timestamp: new Date().toISOString(), 
        relatedUserIds: allMembers, 
        groupId: groupId 
      };
      setActivities(prev => [newActivity, ...prev.filter(a => a.id !== newActivity.id)]);
      dataService.logActivity(newActivity).catch(e => console.error(t('authentication_error'), e));
    } catch (err) {
      console.error("updateGroup error:", err);
      if (previousGroup) {
        setGroups(prev => prev.map(g => g.id === groupId ? previousGroup : g));
      }
      throw err;
    }
  }, [groups, ensureFriendships, currentUser.id, t]);

  const deleteGroup = useCallback(async (groupId: string) => {
    const groupToDelete = groups.find(g => g.id === groupId);
    if (!groupToDelete) return;
    // Optimistically update React state immediately
    setGroups(prev => prev.filter(g => g.id !== groupId));
    setExpenses(prev => prev.filter(e => e.groupId !== groupId));

    try {
      await dataService.deleteExpensesByGroupId(groupId); await dataService.deleteGroup(groupId);
      const newActivity: ActivityLog = { id: `act_gd_${Date.now()}`, userId: currentUser.id, action: `${t('action_deleted')} ${t('group')}`, details: groupToDelete.name, timestamp: new Date().toISOString(), relatedUserIds: groupToDelete.members };
      setActivities(prev => [newActivity, ...prev.filter(a => a.id !== newActivity.id)]);
      dataService.logActivity(newActivity).catch(e => console.error(t('authentication_error'), e));
      addNotification?.(`${t('group')} ${t('action_deleted')}.`, 'success');
    } catch (error) { addNotification?.(t('sync_failed'), 'error'); }
  }, [groups, addNotification, currentUser.id, t]);

  const joinGroup = useCallback(async (groupId: string): Promise<boolean> => {
    let group = groups.find(g => g.id === groupId) || await dataService.getGroupById(groupId);
    if (!group) return false;
    const newMembers = Array.from(new Set([...group.members, currentUser.id]));
    if (!group.members.includes(currentUser.id)) {
      await dataService.updateGroup(groupId, { members: newMembers, lastUpdatedBy: currentUser.id });
      const newActivity: ActivityLog = { id: `act_j_${Date.now()}`, userId: currentUser.id, action: t('join_group'), details: group.name, timestamp: new Date().toISOString(), relatedUserIds: newMembers, groupId: groupId };
      dataService.logActivity(newActivity).catch(e => console.error(t('authentication_error'), e));
    }
    ensureFriendships(newMembers);
    return true;
  }, [groups, currentUser.id, ensureFriendships, t]);

  const settleUp = useCallback(async (payerId: string, receiverId: string, amount: number, currency: string, groupId?: string, recurrence?: RecurrenceInterval, nextRecurringDate?: string) => {
    const payment: Expense = { 
      id: `p_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`, 
      description: t('settlement'), 
      amount: parseFloat(amount.toFixed(2)), 
      currency: currency, 
      date: new Date().toISOString(), 
      payerId: payerId, 
      groupId: groupId, 
      splitWith: [receiverId], 
      category: 'payment', 
      type: 'payment', 
      lastUpdatedBy: currentUser.id,
      recurrence,
      nextRecurringDate
    };
    await addExpense(payment);
  }, [addExpense, t, currentUser.id]);

  const availableCurrencies = useMemo(() => Object.keys(exchangeRates).sort(), [exchangeRates]);
  const getCurrencySymbol = useCallback((code: string) => { try { return (0).toLocaleString('en-US', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/\d/g, '').trim(); } catch (e) { return code; } }, []);
  const getCurrencyName = useCallback((code: string) => { try { return new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) || code; } catch (e) { return code; } }, []);
  const convertToUserCurrency = useCallback((amount: number, fromCurrency: string): number => { if (fromCurrency === userCurrency) return amount; const fromRate = exchangeRates[fromCurrency] || 1; const toRate = exchangeRates[userCurrency] || 1; return (amount / fromRate) * toRate; }, [exchangeRates, userCurrency]);
  const getGroupExpenses = useCallback((groupId: string) => { return expenses.filter(e => e.groupId === groupId); }, [expenses]);

  const getUserBalance = useCallback((userId: string) => {
    return expenses.reduce((balance, e) => {
      const splitWith = e.splitWith || []; const isInvolved = splitWith.includes(userId) || e.payerId === userId;
      if (!isInvolved) return balance;
      const amount = convertToUserCurrency(e.amount, e.currency);
      if (e.type === 'payment' || e.category === 'payment') {
        if (e.payerId === userId) return balance + amount;
        if (splitWith.includes(userId)) return balance - amount;
        return balance;
      } else {
        let myShare = 0;
        if (e.splits && e.splits[userId] !== undefined) myShare = convertToUserCurrency(e.splits[userId], e.currency);
        else if (splitWith.includes(userId)) myShare = amount / Math.max(1, splitWith.length);
        if (e.payerId === userId) return balance + (amount - myShare);
        return balance - myShare;
      }
    }, 0);
  }, [expenses, convertToUserCurrency]);

  const getSplitBalances = useCallback(() => {
    const friendNet: Record<string, number> = {};
    expenses.forEach(e => {
      const amount = convertToUserCurrency(e.amount, e.currency);
      if (e.type === 'payment' || e.category === 'payment') {
        const payer = e.payerId; const receiver = e.splitWith[0];
        if (payer === currentUser.id) { friendNet[receiver] = (friendNet[receiver] || 0) + amount; }
        else if (receiver === currentUser.id) { friendNet[payer] = (friendNet[payer] || 0) - amount; }
      } else {
        const myShare = e.splits && e.splits[currentUser.id] !== undefined ? convertToUserCurrency(e.splits[currentUser.id], e.currency) : (e.splitWith.includes(currentUser.id) ? amount / e.splitWith.length : 0);
        if (e.payerId === currentUser.id) { const owedToMe = amount - myShare; const others = e.splitWith.filter(id => !id || id !== currentUser.id); if (others.length > 0) { const sharePerOther = owedToMe / others.length; others.forEach(id => { friendNet[id] = (friendNet[id] || 0) + sharePerOther; }); } }
        else if (e.splitWith.includes(currentUser.id)) { friendNet[e.payerId] = (friendNet[e.payerId] || 0) - myShare; }
      }
    });
    let totalOwed = 0; let totalOwe = 0;
    Object.values(friendNet).forEach(val => { if (val > 0) totalOwed += val; else if (val < 0) totalOwe += Math.abs(val); });
    return { totalOwed, totalOwe };
  }, [expenses, currentUser.id, convertToUserCurrency]);

  const value = useMemo(() => ({
    currentUser, users, groups, expenses, activities, isAuthenticated, isAuthLoading, isFirebaseActive: isFirebaseEnabled, isSessionChecking, isDataLoading, loginWithGoogle, loginWithEmail, register, resetPassword, loginAsOfflineGuest, logout, updateCurrentUser, appSettings, updateAppSettings, userCurrency, setUserCurrency, availableCurrencies, exchangeRates, isLoadingRates, refreshRates, ratesLastUpdated, isInstallable: !!deferredPrompt || isIOS, isIOS, isStandalone, installApp, showIOSInstallGuide, setShowIOSInstallGuide, addExpense, updateExpense, deleteExpense, addGroup, updateGroup, deleteGroup, joinGroup, addFriend, getGroupExpenses, getUserBalance, getSplitBalances, getCurrencySymbol, getCurrencyName, convertToUserCurrency, settleUp, t, confirmation, requestConfirmation, closeConfirmation, detectRegionSettings, processRecurringExpenses, isDetectingLocation, isOnline, addNotification
  }), [
    currentUser, users, groups, expenses, activities, isAuthenticated, isAuthLoading, isDataLoading, isSessionChecking, appSettings, userCurrency, availableCurrencies, exchangeRates, isLoadingRates, ratesLastUpdated, deferredPrompt, isIOS, isStandalone, showIOSInstallGuide, loginWithGoogle, loginWithEmail, register, resetPassword, loginAsOfflineGuest, logout, updateCurrentUser, updateAppSettings, setUserCurrency, refreshRates, addExpense, updateExpense, deleteExpense, addGroup, updateGroup, deleteGroup, joinGroup, addFriend, getGroupExpenses, getUserBalance, getSplitBalances, getCurrencySymbol, getCurrencyName, convertToUserCurrency, settleUp, installApp, t, confirmation, requestConfirmation, closeConfirmation, detectRegionSettings, processRecurringExpenses, isDetectingLocation, isOnline, addNotification
  ]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};
